import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { GetForecastDetailResponse } from 'src/api/rpc/hooks/procurement/useForecastDetail';
import {
  DetailPanel,
  DividedGroups,
  Field,
  FieldGroup,
  NotInRunNote,
  PanelNote,
} from './DetailPanels';
import {
  customerSalesReasonLabel,
  customerSalesView,
  demandBasisLabel,
  distinctMapped,
  effectivePolicySourceLabel,
  forecastMethodLabel,
  moreLabel,
  percentRangeLabel,
  rangeLabel,
  setLabel,
  stockFlag,
} from './panelFormat';
import { StockSurplusFields } from './StockSurplusFields';
import { PlanningLevelsTrace } from './PlanningLevelsTrace';

type Detail = GetForecastDetailResponse;

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function formatDateTime(value: string | null | undefined): string | null {
  return value ? dateTimeFormatter.format(new Date(value)) : null;
}

function catalogStatusLabel(
  value: boolean | null | undefined,
  gt: ReturnType<typeof useGT>,
): string | null {
  if (value === null || value === undefined) return null;
  return value
    ? gt('Active', { context: 'forecast detail product catalog status' })
    : gt('Inactive', { context: 'forecast detail product catalog status' });
}

function sellableLabel(
  value: boolean | null | undefined,
  gt: ReturnType<typeof useGT>,
): string | null {
  if (value === null || value === undefined) return null;
  return value
    ? gt('Yes', { context: 'forecast detail product sellable value' })
    : gt('No', { context: 'forecast detail product sellable value' });
}

export function PositionPanel({
  detail,
  extraLocationNames,
}: {
  detail?: Detail;
  /** Names for locations outside the scope, such as served spokes. */
  extraLocationNames?: ReadonlyMap<string, string>;
}) {
  const gt = useGT();
  const position = detail?.position;
  return (
    <DetailPanel
      title={<T context="forecast detail panel title">Stock position</T>}
      footer={<CountedAtDetail detail={detail} />}
    >
      <Field
        label={<T context="forecast detail field">On hand</T>}
        value={position?.onHand}
        emphasis
        hint={gt(
          'The latest stock-feed snapshot at each location in scope, added together. Source: the AS400/GAINS stock feed.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Reserved</T>}
        value={position?.reserved}
        hint={gt(
          'Quantity already committed to orders in that same snapshot. Source: the AS400/GAINS stock feed.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">On order</T>}
        value={position?.onOrder}
        hint={gt(
          'Open purchase order lines plus open transfer order lines still due in to these locations.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Available</T>}
        value={position?.available}
        emphasis
        hint={gt('On hand minus reserved. On order is not included.', {
          context: 'forecast detail field hint',
        })}
      />
      <StockSurplusFields
        result={detail?.stockSurplus}
        locationNames={locationNamesOf(detail, extraLocationNames)}
      />
    </DetailPanel>
  );
}

/**
 * Human names for every location the panel may mention: the scope's own, plus
 * any the caller already holds (the served spokes a hub's demand includes).
 */
function locationNamesOf(
  detail: Detail | undefined,
  extra: ReadonlyMap<string, string> | undefined,
): ReadonlyMap<string, string> {
  const names = new Map<string, string>(extra);
  for (const location of detail?.scope.locations ?? []) {
    const name = location.warehouseName ?? location.warehouseCode;
    if (name) names.set(location.warehouseId, name);
  }
  return names;
}

/**
 * Where and when the stock on hand was read, folded under the stock position.
 *
 * A bare "1 / 2" told planners nothing, so it opens into the latest snapshot
 * behind each location: which one, on what date, and how much it held. These
 * are daily stock-file snapshots, not physical cycle counts. The hub-and-spoke
 * rollup — counts at every spoke a hub serves — waits on the replenishment path
 * rework, so it is not shown here.
 */
function CountedAtDetail({ detail }: { detail?: Detail }) {
  const gt = useGT();
  if (!detail) return null;
  const position = detail.position;
  const total = detail.scope.locations.length;
  const names = new Map(
    detail.scope.locations.map((location) => [
      location.warehouseId,
      location.warehouseName ?? location.warehouseCode ?? location.warehouseId,
    ]),
  );
  return (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
        {position.snapshotDate
          ? gt('Stock as of {date} · {counted} of {total} locations', {
              context: 'forecast detail stock snapshot disclosure summary',
              date: position.snapshotDate,
              counted: String(position.countedLocations),
              total: String(total),
            })
          : gt('No stock snapshot', {
              context: 'forecast detail stock snapshot disclosure summary',
            })}
      </summary>
      {position.snapshots.length > 0 ? (
        <table className="mt-1 w-full">
          <thead>
            <tr className="text-muted-foreground">
              <th className="py-0.5 text-left font-normal">
                <T context="forecast detail stock snapshot column">Location</T>
              </th>
              <th className="py-0.5 text-right font-normal">
                <T context="forecast detail stock snapshot column">As of</T>
              </th>
              <th className="py-0.5 text-right font-normal">
                <T context="forecast detail stock snapshot column">On hand</T>
              </th>
            </tr>
          </thead>
          <tbody>
            {position.snapshots.map((snapshot) => (
              <tr key={snapshot.warehouseId} className="border-t">
                <td className="py-0.5 pr-2">
                  {names.get(snapshot.warehouseId) ?? snapshot.warehouseId}
                </td>
                <td className="py-0.5 text-right tabular-nums">
                  {snapshot.snapshotDate}
                </td>
                <td className="py-0.5 text-right tabular-nums">
                  {snapshot.onHand === null
                    ? '—'
                    : Math.round(snapshot.onHand).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <p className="mt-1 leading-snug text-muted-foreground">
        <T context="forecast detail stock snapshot provenance">
          The latest row of the stock feed at each location, dated when it was
          loaded — not a physical cycle count. Locations with no row are not
          listed.
        </T>
      </p>
    </details>
  );
}

export function LevelsPanel({ detail }: { detail?: Detail }) {
  const gt = useGT();
  const policy = detail?.policy;
  const more = moreLabel(gt);
  const planningLevels =
    detail?.scope.locations.length === 1 ? policy?.planningLevels : null;
  return (
    <DetailPanel
      title={<T context="forecast detail panel title">Planning levels</T>}
      footer={
        planningLevels && policy ? (
          <PlanningLevelsTrace levels={planningLevels} policy={policy} />
        ) : null
      }
    >
      <Field
        label={<T context="forecast detail field">Min</T>}
        overrideId="policy.minQuantity"
        value={policy?.minQuantity}
        hint={gt(
          'The reorder point written for the source system’s min, or a buyer’s min override. Source: inventory policy.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Max</T>}
        overrideId="policy.maxQuantity"
        value={policy?.maxQuantity}
        hint={gt(
          'The order-up-to level written for the source system’s max, or a buyer’s max override. Source: inventory policy.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Safety Stock</T>}
        overrideId="policy.safetyStock"
        value={policy?.safetyStock}
        hint={gt(
          'Buffer for demand variability over the lead time: service-level z × monthly demand variability × √(lead time in months), or a negative-binomial quantile for intermittent demand.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Reorder point</T>}
        value={policy?.reorderPoint}
        hint={gt('Forecast demand over the lead time plus safety stock.', {
          context: 'forecast detail field hint',
        })}
      />
      {!planningLevels && (
        <Field
          label={<T context="forecast detail field">Order up to</T>}
          value={policy?.orderUpToLevel}
          emphasis
          hint={gt(
            'Forecast demand over the lead time plus a 30-day review period, plus safety stock — never below the reorder point or the min floor.',
            { context: 'forecast detail field hint' },
          )}
        />
      )}
      <Field
        label={<T context="forecast detail field">Min Floor</T>}
        overrideId="policy.minFloor"
        value={policy?.minFloor}
        hint={gt(
          'A buyer-set lower bound. The trigger level and order-up-to never go below it.',
          { context: 'forecast detail field hint' },
        )}
      />
      {!planningLevels && (
        <Field
          label={<T context="forecast detail field">Effective Protection</T>}
          value={policy?.effectiveTriggerLevel}
          emphasis={(policy?.minFloorAppliedCount ?? 0) > 0}
          hint={gt(
            'The level replenishment protects: safety stock when a forecast is active, otherwise the min — raised to the min floor where that is higher.',
            { context: 'forecast detail field hint' },
          )}
        />
      )}
      <Field
        label={<T context="forecast detail field">Target availability</T>}
        value={percentRangeLabel(policy?.targetAvailability)}
        hint={gt(
          'The in-stock probability safety stock is sized for. A span means the locations in scope do not agree.',
          { context: 'forecast detail field hint' },
        )}
      />
      {!planningLevels && (
        <Field
          label={<T context="forecast detail field">Set by</T>}
          value={setLabel(
            policy?.effectiveLevelSources.map((source) =>
              effectivePolicySourceLabel(source, gt),
            ),
            more,
          )}
          hint={gt(
            'Whether the levels come from the forecast, a safety stock override, a min override, or the calculated min.',
            { context: 'forecast detail field hint' },
          )}
        />
      )}
      <Field
        label={<T context="forecast detail field">Overridden at</T>}
        value={
          policy
            ? `${policy.activeOverrideCount} / ${policy.policyCount}`
            : null
        }
        hint={gt(
          'Locations with a buyer’s planning override in force, out of locations with a policy.',
          { context: 'forecast detail field hint' },
        )}
      />
    </DetailPanel>
  );
}

/**
 * How the policy row was last set, by its stored `calculation_method`.
 * Unknown values print as stored.
 */
function calculationMethodLabel(
  method: string,
  gt: ReturnType<typeof useGT>,
): string {
  switch (method) {
    case 'forecast':
      return gt('Forecast run', {
        context: 'forecast detail policy calculation method',
      });
    case 'imported':
      return gt('Imported from source system', {
        context: 'forecast detail policy calculation method',
      });
    case 'manual':
      return gt('Manual', {
        context: 'forecast detail policy calculation method',
      });
    default:
      return method;
  }
}

/**
 * Demand behavior, forecast, customer sales and the current policy as one
 * row, for pages that show the item's attributes without Forecast Detail's
 * layout.
 */
export function DemandBehaviourPanel({ detail }: { detail?: Detail }) {
  return (
    <div className="space-y-3">
      <DividedGroups>
        <DemandBehaviorGroup detail={detail} />
        <ForecastGroup detail={detail} />
        <CustomerSalesGroup detail={detail} />
        <CurrentPolicyGroup detail={detail} />
      </DividedGroups>
      <MixedDemandBasisNote detail={detail} />
      {detail && !detail.inRun ? <NotInRunNote /> : null}
    </div>
  );
}

/**
 * How the item sells and how it is classed at these locations — the facts a
 * planner checks before trusting a forecast, read beside the planning levels.
 */
export function DemandBehaviorGroup({ detail }: { detail?: Detail }) {
  const gt = useGT();
  const more = moreLabel(gt);
  const identity = detail?.identity;
  return (
    <FieldGroup
      title={<T context="forecast detail panel title">Demand behavior</T>}
    >
      <Field
        label={<T context="forecast detail field">Demand class</T>}
        value={setLabel(detail?.run?.demandClasses, more)}
        hint={gt(
          'How regularly the item sells (smooth, erratic, intermittent, lumpy), from the average gap between sales and how much sale sizes vary. It decides the method.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Stocked</T>}
        value={setLabel(
          distinctMapped(identity?.stockStatuses, stockFlag),
          more,
        )}
        hint={gt(
          'Y = stocked, N = non-stocked, per location. Two values mean the locations in scope differ. Source: item classification from AS400.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Buyer code</T>}
        value={setLabel(identity?.plannerCodes, more)}
        hint={gt(
          'The AS400 buyer code on the item. This is not an inventory planner code.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Pop</T>}
        value={setLabel(identity?.popularityCodes, more)}
        hint={gt(
          'Popularity class (A–E) per location. Source: item classification from AS400.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Catalog status</T>}
        value={catalogStatusLabel(identity?.productIsActive, gt)}
        hint={gt('Whether the item is active in the product catalog.', {
          context: 'forecast detail field hint',
        })}
      />
      <Field
        label={<T context="forecast detail field">Sellable</T>}
        value={sellableLabel(identity?.productIsSellable, gt)}
        hint={gt('Whether the product catalog marks the item sellable.', {
          context: 'forecast detail field hint',
        })}
      />
      <Field
        label={<T context="forecast detail field">Replenishes from</T>}
        value={setLabel(identity?.replenishesFrom, more)}
        hint={gt(
          'The locations that supply this item to the ones in scope. Source: the preferred lanes of the published supply network.',
          { context: 'forecast detail field hint' },
        )}
      />
    </FieldGroup>
  );
}

/** How the selected run forecast the item, and whether it is approved. */
export function ForecastGroup({
  detail,
  approval,
}: {
  detail?: Detail;
  approval?: ReactNode;
}) {
  const gt = useGT();
  const more = moreLabel(gt);
  return (
    <FieldGroup title={<T context="forecast detail field group">Forecast</T>}>
      <Field
        label={<T context="forecast detail field">Method</T>}
        value={setLabel(
          detail?.run?.methods.map((method) => forecastMethodLabel(method, gt)),
          more,
        )}
        hint={gt(
          'The model the selected run used. 12-month rolling average: the mean of the last 12 months. Intermittent demand average (TSB): how often the item sells × how much it sells when it does, decaying through months with no sales. Seasonal trend (Holt-Winters): level, trend and seasonality, used only for steady demand with 24+ months of history.',
          { context: 'forecast detail method explanation' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Demand basis</T>}
        value={setLabel(
          detail?.run?.demandBases.map((basis) => demandBasisLabel(basis, gt)),
          more,
        )}
        hint={gt(
          'What the selected run forecast from. Outbound history (legacy): each location’s sales plus its transfers out. Served customer sales: customer sales at the location plus the spokes it serves. A saved run keeps the basis it was run on.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Edited history at</T>}
        value={detail?.run?.overrideAppliedCount}
        hint={gt(
          'Locations whose history the run consumed with an active edit.',
          { context: 'forecast detail field hint' },
        )}
      />
      {approval ? <div className="col-span-2 pt-1">{approval}</div> : null}
    </FieldGroup>
  );
}

/** The scope mixes demand bases, so its demand totals do too. */
export function MixedDemandBasisNote({ detail }: { detail?: Detail }) {
  if ((detail?.run?.demandBases.length ?? 0) <= 1) return null;
  return (
    <p
      role="note"
      className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
    >
      <T context="forecast detail mixed demand basis warning">
        The locations in scope were forecast on different demand bases, so their
        demand totals mix them. Filter to locations that share a basis to
        compare like with like.
      </T>
    </p>
  );
}

/**
 * The current policy the forecast feeds. The spoke count drops out where the
 * spokes themselves are listed, so the number is said once.
 */
export function CurrentPolicyGroup({
  detail,
  showSpokeCount = true,
}: {
  detail?: Detail;
  showSpokeCount?: boolean;
}) {
  const gt = useGT();
  const more = moreLabel(gt);
  const policy = detail?.policy;
  const derived = detail?.derived;
  return (
    <FieldGroup
      title={<T context="forecast detail field group">Current policy</T>}
    >
      <Field
        label={<T context="forecast detail field">Calculated by</T>}
        value={setLabel(
          policy?.calculationMethods.map((method) =>
            calculationMethodLabel(method, gt),
          ),
          more,
        )}
        hint={gt(
          'What last set the planning levels: a forecast run, an import from the source system, or a manual entry.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Last calculated</T>}
        value={formatDateTime(policy?.lastCalculatedAt)}
        hint={gt(
          'When the planning levels were last recalculated — the oldest across the scope. A dash means no date was recorded.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Min floor applied at</T>}
        value={policy?.minFloorAppliedCount}
        hint={gt(
          'Locations where the min floor is above the calculated trigger level, so the floor governs.',
          { context: 'forecast detail field hint' },
        )}
      />
      {showSpokeCount ? (
        <Field
          label={<T context="forecast detail field">Spokes served</T>}
          value={derived?.dependentSpokeCount}
          hint={gt(
            'Locations that a lane in the published supply network leads to from a location in scope, and that are not in scope themselves.',
            { context: 'forecast detail field hint' },
          )}
        />
      ) : null}
    </FieldGroup>
  );
}

/**
 * Hits and demand size, counted from customer sales lines rather than the
 * monthly history: a hit is an order with a positive sale, each order counted
 * once however many lines or imports carry it. When the source cannot count
 * them once it says why, and nothing stands in for the numbers.
 */
export function CustomerSalesGroup({ detail }: { detail?: Detail }) {
  const gt = useGT();
  const view = customerSalesView(detail?.derived);
  const ordersHint = (months: string) =>
    gt(
      'Distinct customer orders with a positive sale of this item in the last {months} complete months, at the locations in scope — and, for a hub forecast on served demand, the spokes it served. Transfers are not customer sales. Source: customer sales lines, each order counted once.',
      { context: 'forecast detail field hint', months },
    );
  return (
    <FieldGroup
      title={
        <span className="flex items-center gap-1">
          <T context="forecast detail field group">Customer sales</T>
          {view.unavailableReason ? (
            <span className="font-normal text-muted-foreground">
              <T context="forecast detail customer sales unavailable tag">
                · unavailable
              </T>
            </span>
          ) : null}
        </span>
      }
    >
      {view.unavailableReason ? (
        <PanelNote>
          {customerSalesReasonLabel(view.unavailableReason, gt)}
        </PanelNote>
      ) : (
        <CustomerSalesFields view={view} ordersHint={ordersHint} />
      )}
    </FieldGroup>
  );
}

function CustomerSalesFields({
  view,
  ordersHint,
}: {
  view: ReturnType<typeof customerSalesView>;
  ordersHint: (months: string) => string;
}) {
  const gt = useGT();
  return (
    <>
      <Field
        label={<T context="forecast detail field">Hits (orders), past 6</T>}
        value={view.hits6}
        hint={ordersHint('6')}
      />
      <Field
        label={<T context="forecast detail field">Hits (orders), past 9</T>}
        value={view.hits9}
        hint={ordersHint('9')}
      />
      <Field
        label={<T context="forecast detail field">Hits (orders), past 12</T>}
        value={view.hits12}
        hint={ordersHint('12')}
      />
      <Field
        label={
          <T context="forecast detail field">Hits (orders), year before</T>
        }
        value={view.priorYearHits}
        hint={gt(
          'The same order count for the 12 months before the past 12 — the year-ago comparison.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Avg order size</T>}
        value={view.avgOrderSize}
        hint={gt(
          'Net customer quantity over the past 12 months (returns subtracted) divided by the orders with a positive sale.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Through</T>}
        value={view.endMonth && formatMonth(view.endMonth)}
        hint={gt(
          'The last month counted: the selected run’s last history month, or last month when no run covers these locations.',
          { context: 'forecast detail field hint' },
        )}
      />
    </>
  );
}

const monthFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

function formatMonth(month: string): string {
  return monthFormatter.format(new Date(`${month}T00:00:00Z`));
}

export function LeadTimePanel({ detail }: { detail?: Detail }) {
  const gt = useGT();
  const run = detail?.run;
  const more = moreLabel(gt);
  return (
    <DetailPanel title={<T context="forecast detail panel title">Lead time</T>}>
      <Field
        label={<T context="forecast detail field">Lead time, days</T>}
        overrideId="leadTime.days"
        value={rangeLabel(run?.leadTimeDaysUsed)}
        hint={gt(
          'The lead time the selected run sized the levels with. A span means the locations in scope do not agree.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Variability, days</T>}
        value={rangeLabel(run?.leadTimeSigmaDays)}
        hint={gt('Standard deviation of the sampled receipt lead times.', {
          context: 'forecast detail field hint',
        })}
      />
      <Field
        label={<T context="forecast detail field">Measured at</T>}
        value={setLabel(run?.leadTimeSources, more)}
        hint={gt(
          'Which rung of the lead-time ladder this came from; "override" is a manual pin, "none" means assumed.',
          { context: 'forecast detail field hint' },
        )}
      />
      <Field
        label={<T context="forecast detail field">Receipts sampled</T>}
        value={run?.leadTimeSampleN}
        hint={gt(
          'Purchase order receipts whose order-to-receipt time was measured, summed across the scope.',
          { context: 'forecast detail field hint' },
        )}
      />
    </DetailPanel>
  );
}

/**
 * The strip above the filters: the way back, which run is shown, and whether
 * that run holds this item at all.
 *
 * It no longer names the item or the location. Both are controls now, sitting
 * immediately below — a title repeating a dropdown's value is a second place
 * for the same fact to be wrong, and it pushed the thing you came here to
 * change below the fold of the thing you came here to read.
 */
export function DetailHeader({
  detail,
  runLabel,
  onBack,
}: {
  detail?: Detail;
  /** Formatted label for the run being shown; absent until the run resolves. */
  runLabel?: string;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeftIcon className="h-4 w-4" />
        <T context="forecast detail, back to the demand grid">Demand grid</T>
      </Button>
      {runLabel ? (
        <span className="text-xs text-muted-foreground">{runLabel}</span>
      ) : null}
      {detail && !detail.inRun ? (
        <Badge variant="outline">
          <T context="forecast detail badge, item absent from run">
            Not in this run
          </T>
        </Badge>
      ) : null}
    </div>
  );
}
